import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Sidebar from '@/components/layout/Sidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  CreditCard, 
  DollarSign, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  ArrowUpRight,
  RefreshCw,
  Truck,
  FileText,
  ThumbsUp,
  ThumbsDown
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'Pending', icon: Clock, color: 'text-amber-400', bg: 'bg-amber-950/50 border-amber-900/50' },
  processing: { label: 'Processing', icon: RefreshCw, color: 'text-blue-400', bg: 'bg-blue-950/50 border-blue-900/50' },
  completed: { label: 'Completed', icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-950/50 border-emerald-900/50' },
  failed: { label: 'Failed', icon: XCircle, color: 'text-red-400', bg: 'bg-red-950/50 border-red-900/50' },
  approved: { label: 'Approved', icon: ThumbsUp, color: 'text-emerald-400', bg: 'bg-emerald-950/50 border-emerald-900/50' },
  rejected: { label: 'Rejected', icon: ThumbsDown, color: 'text-red-400', bg: 'bg-red-950/50 border-red-900/50' },
  refunded: { label: 'Refunded', icon: ArrowUpRight, color: 'text-zinc-400', bg: 'bg-zinc-800 border-zinc-700' },
};

const PaymentsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [payments, setPayments] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedShipment, setSelectedShipment] = useState('');
  const [payoutData, setPayoutData] = useState({ driver_id: '', shipment_id: '', amount: '', description: '' });
  const [rejectionReason, setRejectionReason] = useState('');
  const [stats, setStats] = useState(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [paymentsRes, shipmentsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/payments`),
        axios.get(`${API}/shipments`),
        axios.get(`${API}/analytics/overview`)
      ]);
      setPayments(paymentsRes.data);
      setShipments(shipmentsRes.data);
      setStats(analyticsRes.data);

      if (user?.role === 'dispatcher' || user?.role === 'distributor') {
        const driversRes = await axios.get(`${API}/users/drivers`);
        setDrivers(driversRes.data);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setIsLoading(false);
    }
  }, [user?.role]);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      toast.info('Payment status check timed out. Please refresh to check status.');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Thank you.');
        fetchData();
        navigate('/payments', { replace: true });
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        navigate('/payments', { replace: true });
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  }, [fetchData, navigate]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams, pollPaymentStatus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreatePayment = async () => {
    if (!selectedShipment) {
      toast.error('Please select a shipment');
      return;
    }

    try {
      const response = await axios.post(`${API}/payments/create-checkout`, {
        shipment_id: selectedShipment,
        payment_type: 'shipment',
        origin_url: window.location.origin
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payment');
    }
  };

  const handleCreatePayout = async () => {
    if (!payoutData.driver_id || !payoutData.shipment_id || !payoutData.amount) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      await axios.post(`${API}/payments/driver-payout`, {
        ...payoutData,
        amount: parseFloat(payoutData.amount)
      });
      toast.success('Driver payout created and pending approval');
      setPayoutDialogOpen(false);
      setPayoutData({ driver_id: '', shipment_id: '', amount: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payout');
    }
  };

  const handleApproveReject = async (action) => {
    if (!selectedPayment) return;

    try {
      await axios.post(`${API}/payments/approve-reject`, {
        payment_id: selectedPayment.id,
        action,
        reason: action === 'reject' ? rejectionReason : null
      });
      toast.success(`Payment ${action}d successfully`);
      setApprovalDialogOpen(false);
      setSelectedPayment(null);
      setRejectionReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} payment`);
    }
  };

  const eligibleShipments = shipments.filter(s => s.value && s.value > 0);
  const pendingApprovalPayments = payments.filter(p => p.status === 'pending' && p.payment_type === 'driver_payout');

  return (
    <div className="min-h-screen bg-background">
      <Sidebar role={user?.role} />
      <main className="lg:ml-64 p-6 lg:p-10" data-testid="payments-page">
        <div className="space-y-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-4xl font-heading font-bold tracking-tight">Payments</h1>
              <p className="text-muted-foreground mt-1">Manage payments, payouts, and transactions</p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={fetchData} disabled={isLoading} data-testid="refresh-payments-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              
              {user?.role === 'distributor' && (
                <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-primary hover:bg-primary/90 shadow-glow" data-testid="pay-shipment-btn">
                      <CreditCard className="w-4 h-4 mr-2" />
                      Pay for Shipment
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-heading text-2xl">Pay for Shipment</DialogTitle>
                      <DialogDescription>Select a shipment to process payment. BOL must be fully signed.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Select Shipment</Label>
                        <Select value={selectedShipment} onValueChange={setSelectedShipment}>
                          <SelectTrigger data-testid="payment-shipment-select">
                            <SelectValue placeholder="Choose a shipment" />
                          </SelectTrigger>
                          <SelectContent>
                            {eligibleShipments.length === 0 ? (
                              <SelectItem value="none" disabled>No eligible shipments</SelectItem>
                            ) : (
                              eligibleShipments.map((shipment) => (
                                <SelectItem key={shipment.id} value={shipment.id}>
                                  {shipment.tracking_number} - ${shipment.value?.toLocaleString()}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {selectedShipment && (
                        <div className="p-4 bg-muted rounded-lg">
                          {(() => {
                            const s = shipments.find(sh => sh.id === selectedShipment);
                            return s ? (
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Route:</span>
                                  <span>{s.origin} → {s.destination}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Amount:</span>
                                  <span className="font-bold text-primary">${s.value?.toLocaleString()}</span>
                                </div>
                              </div>
                            ) : null;
                          })()}
                        </div>
                      )}
                      
                      <div className="p-3 bg-amber-950/30 border border-amber-900/50 rounded-lg">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5" />
                          <p className="text-sm text-amber-200">
                            All parties (shipper, carrier, consignee) must sign the Bill of Lading before payment can be processed.
                          </p>
                        </div>
                      </div>
                      
                      <Button 
                        className="w-full bg-primary hover:bg-primary/90"
                        onClick={handleCreatePayment}
                        disabled={!selectedShipment}
                        data-testid="process-payment-btn"
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Proceed to Payment
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}

              {(user?.role === 'dispatcher' || user?.role === 'distributor') && (
                <Dialog open={payoutDialogOpen} onOpenChange={setPayoutDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="border-primary/30 text-primary" data-testid="create-payout-btn">
                      <Truck className="w-4 h-4 mr-2" />
                      Driver Payout
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-heading text-2xl">Create Driver Payout</DialogTitle>
                      <DialogDescription>Create a payout for a driver. Requires approval before processing.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Select Driver</Label>
                        <Select value={payoutData.driver_id} onValueChange={(v) => setPayoutData({...payoutData, driver_id: v})}>
                          <SelectTrigger data-testid="payout-driver-select">
                            <SelectValue placeholder="Choose a driver" />
                          </SelectTrigger>
                          <SelectContent>
                            {drivers.map((driver) => (
                              <SelectItem key={driver.id} value={driver.id}>{driver.name} ({driver.email})</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Select Shipment</Label>
                        <Select value={payoutData.shipment_id} onValueChange={(v) => setPayoutData({...payoutData, shipment_id: v})}>
                          <SelectTrigger data-testid="payout-shipment-select">
                            <SelectValue placeholder="Choose a shipment" />
                          </SelectTrigger>
                          <SelectContent>
                            {shipments.filter(s => s.status === 'delivered').map((shipment) => (
                              <SelectItem key={shipment.id} value={shipment.id}>{shipment.tracking_number}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Amount ($)</Label>
                        <Input type="number" placeholder="0.00" value={payoutData.amount} onChange={(e) => setPayoutData({...payoutData, amount: e.target.value})} data-testid="payout-amount-input" />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Description (Optional)</Label>
                        <Textarea placeholder="Payout description..." value={payoutData.description} onChange={(e) => setPayoutData({...payoutData, description: e.target.value})} data-testid="payout-description-input" />
                      </div>
                      
                      <Button className="w-full bg-primary hover:bg-primary/90" onClick={handleCreatePayout} data-testid="submit-payout-btn">Create Payout Request</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="card-hover">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-primary/10"><CreditCard className="w-6 h-6 text-primary" /></div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Payments</p>
                    <p className="text-3xl font-heading font-bold">{stats?.total_payments || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-emerald-950/50"><CheckCircle className="w-6 h-6 text-emerald-400" /></div>
                  <div>
                    <p className="text-sm text-muted-foreground">Completed</p>
                    <p className="text-3xl font-heading font-bold">{stats?.completed_payments || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-amber-950/50"><Clock className="w-6 h-6 text-amber-400" /></div>
                  <div>
                    <p className="text-sm text-muted-foreground">Pending</p>
                    <p className="text-3xl font-heading font-bold">{stats?.pending_payments || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-primary/10"><DollarSign className="w-6 h-6 text-primary" /></div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Value</p>
                    <p className="text-3xl font-heading font-bold">${(stats?.total_value || 0).toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {pendingApprovalPayments.length > 0 && (user?.role === 'dispatcher' || user?.role === 'distributor') && (
            <Card className="card-hover border-l-4 border-l-amber-500">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-400" />
                  Pending Approvals
                </CardTitle>
                <CardDescription>Driver payouts awaiting your approval</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pendingApprovalPayments.map((payment) => (
                    <div key={payment.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div>
                        <p className="font-medium">{payment.payment_number}</p>
                        <p className="text-sm text-muted-foreground">{payment.description}</p>
                        <p className="text-sm text-muted-foreground">Driver: {payment.metadata?.driver_name}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <p className="text-xl font-bold text-primary">${payment.amount?.toLocaleString()}</p>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-950/50" onClick={() => { setSelectedPayment(payment); handleApproveReject('approve'); }} data-testid={`approve-${payment.id}`}>
                            <ThumbsUp className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-950/50" onClick={() => { setSelectedPayment(payment); setApprovalDialogOpen(true); }} data-testid={`reject-${payment.id}`}>
                            <ThumbsDown className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="card-hover">
            <CardHeader>
              <CardTitle className="font-heading">Payment History</CardTitle>
              <CardDescription>All your transactions and payouts</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Payment #</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-8">No payments yet</TableCell>
                      </TableRow>
                    ) : (
                      payments.map((payment) => {
                        const config = statusConfig[payment.status] || statusConfig.pending;
                        return (
                          <TableRow key={payment.id} data-testid={`payment-row-${payment.id}`}>
                            <TableCell className="font-mono text-sm">{payment.payment_number}</TableCell>
                            <TableCell><span className="capitalize">{payment.payment_type?.replace('_', ' ')}</span></TableCell>
                            <TableCell className="max-w-xs truncate">{payment.description}</TableCell>
                            <TableCell className="font-bold">${payment.amount?.toLocaleString()}</TableCell>
                            <TableCell>
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold uppercase border ${config.bg}`}>
                                <config.icon className={`w-3 h-3 ${config.color}`} />
                                <span className={config.color}>{config.label}</span>
                              </span>
                            </TableCell>
                            <TableCell className="text-muted-foreground">{new Date(payment.created_at).toLocaleDateString()}</TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Reject Payment</DialogTitle>
                <DialogDescription>Please provide a reason for rejecting this payment</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Rejection Reason</Label>
                  <Textarea placeholder="Enter reason for rejection..." value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} data-testid="rejection-reason-input" />
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" className="flex-1" onClick={() => setApprovalDialogOpen(false)}>Cancel</Button>
                  <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={() => handleApproveReject('reject')} data-testid="confirm-reject-btn">Reject Payment</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </div>
  );
};

export default PaymentsPage;
